import { useQuery } from '@tanstack/react-query';

import { type TokenDetails } from '@app/client/common/structs/marketplace';
import { type Wishlist } from '@app/client/common/structs/wishlists/wishlists';
import { callGetTokenDetailsList } from '@app/client/resources/marketplace';
import { YACS_TOKENS_LIST_CACHE } from '@app/client/resources/marketplace/types';
import { callGetNFTCollectionDetails } from '@app/client/resources/yat-pages/page-items';

import { checkValidContractAddress } from './addresses';

type GetTokenDataWithFallbacksProps = {
    items: Wishlist[];
    disabled?: boolean;
};

export const useGetTokenDataWithFallbacks = ({ items, disabled = false }: GetTokenDataWithFallbacksProps) => {
    const tokenAddresses: string[] = [];
    const tokenDetailsProps = items.reduce<{ [key: string]: string[] }>((acc, curr) => {
        const { contract, token_id } = curr.item.Token || {};
        if (contract && token_id) {
            if (!acc[contract]) acc[contract] = [];
            acc[contract].push(token_id);
            tokenAddresses.push(`${checkValidContractAddress(contract)}:${token_id}`);
        }
        return acc;
    }, {});

    const tokensCache = [YACS_TOKENS_LIST_CACHE, ...tokenAddresses];
    const { data: tokenDataResponse, isLoading: isLoadingTokenData } = useQuery(
        tokensCache,
        () =>
            callGetTokenDetailsList({
                // settings: { signal },
                tokens: Object.keys(tokenDetailsProps).map((contractAddress) => ({
                    contract: checkValidContractAddress(contractAddress),
                    token_ids: tokenDetailsProps[contractAddress],
                })),
            }),
        {
            retry: 2,
            staleTime: Infinity,
            keepPreviousData: true,
            enabled: !disabled && tokenAddresses.length > 0,
        },
    );

    const { data: tokensWithFallback } = useQuery(
        ['fallback', ...tokenAddresses],
        () =>
            callGetNFTCollectionDetails({
                query: { tokens: tokenAddresses.join(',') },
                //settings: { signal }
            }).then((res) => {
                if (tokenDataResponse?.ok && tokenDataResponse?.value)
                    tokenDataResponse.value.contents?.map((token) => {
                        const fallbackData = res.tokens.find(
                            (fallbackData) =>
                                fallbackData.token.tokenId === token.token_id &&
                                fallbackData.token.contract === token.collection,
                        );
                        const fallback = {
                            animation_url: fallbackData?.token.media,
                            description: fallbackData?.token.description,
                            image_url: fallbackData?.token.image,
                            name: fallbackData?.token.name,
                            token_id: fallbackData?.token.tokenId,
                            collection: fallbackData?.token.contract,
                        } as TokenDetails;
                        return { ...token, fallback };
                    });
            }),
        {
            retry: 1,
            keepPreviousData: true,
            staleTime: Infinity,
            enabled: tokenAddresses.length > 0 && !disabled && !isLoadingTokenData,
        },
    );

    const tokens = tokenDataResponse?.ok && tokenDataResponse?.value ? tokenDataResponse?.value?.contents : undefined;

    return {
        tokensWithFallback: tokensWithFallback || tokens,
        isLoading: isLoadingTokenData && tokenAddresses.length > 0,
    };
};
